import { ChangeEvent, useState } from 'react';
import { Maths } from 'helpers/maths';
import {
  calculateCoveragePercent,
  calculateAppliedAcreage,
  calculateTotalArea,
  calculateAverageCoveragePercent,
  distributeTotalAcreageProportionally
} from 'screens/Applications&WorkOrders/StepsLayout/Components/PropertiesStep/helpers/cropZoneMath';
import { CropZoneRecord } from '../types';
import { ensureMinimumValue } from '../helpers';

const formatFocusValue = (value: number) =>
  value.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: 15
  });

/**
 * The "Edit Total Applied Acreage" modal's state and handlers — shared
 * verbatim between Applications/Work Orders and Plans. What happens to the
 * new total *after* it's committed (tank/carrier recalculation for AW,
 * `applyPlannedAreaChange` for Plans) is each screen's own concern, plugged
 * in via `onTotalAcreageChange`.
 */
const useTotalAcreageEditor = <T extends CropZoneRecord>(
  cropZones: T[] | undefined,
  totalAppliedAcreage: number,
  setFieldValue: (field: string, value: unknown) => void,
  onTotalAcreageChange: (total: number) => void
) => {
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [totalAcresDisplay, setTotalAcresDisplay] = useState('');
  const [totalCoveragePercent, setTotalCoveragePercent] = useState('');
  const [totalAcresValue, setTotalAcresValue] = useState(0);
  const [totalCoveragePercentValue, setTotalCoveragePercentValue] = useState(0);
  const [lastEditedField, setLastEditedField] = useState<'acres' | 'coverage'>(
    'acres'
  );

  const handleOpenEditModal = () => {
    setEditModalOpen(true);
    setTotalAcresDisplay(
      Maths.parseFloatRound(totalAppliedAcreage, 2).toFixed(2)
    );
    setTotalAcresValue(totalAppliedAcreage);

    const avgCoverage = calculateAverageCoveragePercent(cropZones || []);
    setTotalCoveragePercent(Maths.parseFloatRound(avgCoverage, 2).toFixed(2));
    setTotalCoveragePercentValue(avgCoverage);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setTotalAcresDisplay('');
    setTotalCoveragePercent('');
  };

  const handleSaveEditModal = () => {
    if (!cropZones || cropZones.length === 0) {
      handleCloseEditModal();
      return;
    }

    const distributionMode =
      lastEditedField === 'coverage' ? 'uniform' : 'proportional';

    const updatedCropZones = distributeTotalAcreageProportionally(
      cropZones,
      totalAcresValue,
      totalCoveragePercentValue,
      distributionMode
    );

    // Update the cropZones in Formik first, then commit the user's exact
    // total. Driving the total update explicitly (rather than relying on a
    // reactive sum-of-zones back-propagation) preserves the user's exact
    // input — the sum of zones may differ by floating-point noise after
    // distribution.
    setFieldValue('cropZones', updatedCropZones);
    onTotalAcreageChange(totalAcresValue);

    handleCloseEditModal();
  };

  const handleTotalAcresBlur = () => {
    const value = Number(totalAcresDisplay);
    if (!Number.isNaN(value)) {
      const adjustedValue = ensureMinimumValue(value);

      setTotalAcresValue(adjustedValue);
      setTotalAcresDisplay(Maths.parseFloatRound(adjustedValue, 2).toFixed(2));

      if (adjustedValue !== value) {
        const totalArea = calculateTotalArea(cropZones || []);
        if (totalArea > 0) {
          const newCoveragePercent = calculateCoveragePercent(
            adjustedValue,
            totalArea
          );
          setTotalCoveragePercent(
            Maths.parseFloatRound(newCoveragePercent, 2).toFixed(2)
          );
          setTotalCoveragePercentValue(newCoveragePercent);
        }
      }
    }
  };

  const handleTotalCoveragePercentBlur = () => {
    const value = Number(totalCoveragePercent);
    if (!Number.isNaN(value)) {
      const adjustedValue = ensureMinimumValue(value);

      setTotalCoveragePercentValue(adjustedValue);
      setTotalCoveragePercent(
        Maths.parseFloatRound(adjustedValue, 2).toFixed(2)
      );

      if (adjustedValue !== value) {
        const totalArea = calculateTotalArea(cropZones || []);
        if (totalArea > 0) {
          const newTotalAcres = calculateAppliedAcreage(
            adjustedValue,
            totalArea
          );
          setTotalAcresDisplay(
            Maths.parseFloatRound(newTotalAcres, 2).toFixed(2)
          );
          setTotalAcresValue(newTotalAcres);
        }
      }
    }
  };

  const handleTotalCoveragePercentFocus = () => {
    setTotalCoveragePercent(formatFocusValue(totalCoveragePercentValue));
  };

  const handleTotalAcresChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setTotalAcresDisplay(inputValue);
    setLastEditedField('acres');

    const numericValue = Number(inputValue);
    if (!Number.isNaN(numericValue)) {
      setTotalAcresValue(numericValue);

      const totalArea = calculateTotalArea(cropZones || []);
      if (totalArea > 0) {
        const newCoveragePercent = calculateCoveragePercent(
          numericValue,
          totalArea
        );
        setTotalCoveragePercent(
          Maths.parseFloatRound(newCoveragePercent, 2).toFixed(2)
        );
        setTotalCoveragePercentValue(newCoveragePercent);
      }
    }
  };

  const handleTotalAcresFocus = () => {
    setTotalAcresDisplay(formatFocusValue(totalAcresValue));
  };

  const handleTotalCoveragePercentChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = e.target.value;
    setTotalCoveragePercent(inputValue);
    setLastEditedField('coverage');

    const numericValue = Number(inputValue);
    if (!Number.isNaN(numericValue)) {
      const adjustedValue = ensureMinimumValue(numericValue);
      setTotalCoveragePercentValue(adjustedValue);

      const totalArea = calculateTotalArea(cropZones || []);
      if (totalArea > 0) {
        const newTotalAcres = calculateAppliedAcreage(adjustedValue, totalArea);
        setTotalAcresDisplay(
          Maths.parseFloatRound(newTotalAcres, 2).toFixed(2)
        );
        setTotalAcresValue(newTotalAcres);
      }
    }
  };

  return {
    isEditModalOpen,
    totalAcresDisplay,
    totalCoveragePercent,
    handleOpenEditModal,
    handleCloseEditModal,
    handleSaveEditModal,
    handleTotalAcresBlur,
    handleTotalAcresChange,
    handleTotalAcresFocus,
    handleTotalCoveragePercentBlur,
    handleTotalCoveragePercentChange,
    handleTotalCoveragePercentFocus
  };
};

export default useTotalAcreageEditor;
